import React from 'react';
import { motion } from 'framer-motion';
import Container from '../components/common/Container';
import {
  CpuChipIcon,
  CubeTransparentIcon,
  CircleStackIcon,
  AdjustmentsHorizontalIcon,
  PuzzlePieceIcon,
  MagnifyingGlassCircleIcon,
  ArrowPathIcon,
  CheckBadgeIcon,
  ServerStackIcon,
  CommandLineIcon,
  DocumentTextIcon,
  SparklesIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';

// ─── Reusable animated section wrapper ───
const AnimatedSection: React.FC<{
  children: React.ReactNode;
  className?: string;
  delay?: number;
}> = ({ children, className = '', delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, delay }}
    viewport={{ once: true }}
    className={className}
  >
    {children}
  </motion.div>
);

// ─── SVG Architecture Diagram: Full System ───
const SystemArchitectureDiagram: React.FC = () => (
  <svg viewBox="0 0 900 520" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="grad-cyan" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0ea5e9" />
        <stop offset="100%" stopColor="#06b6d4" />
      </linearGradient>
      <linearGradient id="grad-violet" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#8b5cf6" />
        <stop offset="100%" stopColor="#3b82f6" />
      </linearGradient>
      <linearGradient id="grad-emerald" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#10b981" />
        <stop offset="100%" stopColor="#06b6d4" />
      </linearGradient>
      <linearGradient id="grad-amber" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f59e0b" />
        <stop offset="100%" stopColor="#f43f5e" />
      </linearGradient>
      <linearGradient id="grad-rose" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f43f5e" />
        <stop offset="100%" stopColor="#8b5cf6" />
      </linearGradient>
      <filter id="glow">
        <feGaussianBlur stdDeviation="3" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#0ea5e9" />
      </marker>
      <marker id="arrowhead-violet" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#8b5cf6" />
      </marker>
      <marker id="arrowhead-emerald" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#10b981" />
      </marker>
      <marker id="arrowhead-amber" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#f59e0b" />
      </marker>
    </defs>

    {/* Background */}
    <rect width="900" height="520" fill="transparent" />

    {/* ── Row 1: AI Model + Vector DB ── */}
    {/* AI Model */}
    <rect x="60" y="30" width="200" height="80" rx="12" fill="#8b5cf6" fillOpacity="0.15" stroke="#8b5cf6" strokeWidth="1.5" />
    <text x="160" y="62" textAnchor="middle" fill="#c4b5fd" fontSize="11" fontWeight="600">AI MODEL</text>
    <text x="160" y="82" textAnchor="middle" fill="#e2e8f0" fontSize="14" fontWeight="700">LLM Engine</text>
    <text x="160" y="100" textAnchor="middle" fill="#94a3b8" fontSize="10">Structured Output / JSON Schema</text>

    {/* Vector DB */}
    <rect x="340" y="30" width="220" height="80" rx="12" fill="#10b981" fillOpacity="0.15" stroke="#10b981" strokeWidth="1.5" />
    <text x="450" y="62" textAnchor="middle" fill="#6ee7b7" fontSize="11" fontWeight="600">VECTOR DATABASE</text>
    <text x="450" y="82" textAnchor="middle" fill="#e2e8f0" fontSize="14" fontWeight="700">Component Registry</text>
    <text x="450" y="100" textAnchor="middle" fill="#94a3b8" fontSize="10">Schemas + Metadata + Embeddings</text>

    {/* Curriculum DB */}
    <rect x="640" y="30" width="200" height="80" rx="12" fill="#0ea5e9" fillOpacity="0.15" stroke="#0ea5e9" strokeWidth="1.5" />
    <text x="740" y="62" textAnchor="middle" fill="#7dd3fc" fontSize="11" fontWeight="600">CURRICULUM DATABASE</text>
    <text x="740" y="82" textAnchor="middle" fill="#e2e8f0" fontSize="14" fontWeight="700">Knowledge Graph</text>
    <text x="740" y="100" textAnchor="middle" fill="#94a3b8" fontSize="10">Modules / Submodules / Prerequisites</text>

    {/* ── Row 2: AI Generation Pipeline ── */}
    <rect x="120" y="160" width="660" height="90" rx="14" fill="#8b5cf6" fillOpacity="0.08" stroke="#8b5cf6" strokeWidth="1" strokeDasharray="6 3" />
    <text x="450" y="182" textAnchor="middle" fill="#c4b5fd" fontSize="12" fontWeight="700">AI QUESTION GENERATION PIPELINE</text>

    {/* Sub-boxes inside pipeline */}
    <rect x="140" y="195" width="145" height="44" rx="8" fill="#1e293b" stroke="#8b5cf6" strokeWidth="1" />
    <text x="212" y="222" textAnchor="middle" fill="#e2e8f0" fontSize="11" fontWeight="600">Query Context</text>

    <rect x="310" y="195" width="145" height="44" rx="8" fill="#1e293b" stroke="#10b981" strokeWidth="1" />
    <text x="382" y="222" textAnchor="middle" fill="#e2e8f0" fontSize="11" fontWeight="600">Component Search</text>

    <rect x="480" y="195" width="145" height="44" rx="8" fill="#1e293b" stroke="#8b5cf6" strokeWidth="1" />
    <text x="552" y="222" textAnchor="middle" fill="#e2e8f0" fontSize="11" fontWeight="600">Schema Assembly</text>

    <rect x="650" y="195" width="110" height="44" rx="8" fill="#1e293b" stroke="#f59e0b" strokeWidth="1" />
    <text x="705" y="222" textAnchor="middle" fill="#e2e8f0" fontSize="11" fontWeight="600">Validation</text>

    {/* Arrows within pipeline */}
    <line x1="285" y1="217" x2="308" y2="217" stroke="#8b5cf6" strokeWidth="1.5" markerEnd="url(#arrowhead-violet)" />
    <line x1="455" y1="217" x2="478" y2="217" stroke="#10b981" strokeWidth="1.5" markerEnd="url(#arrowhead-emerald)" />
    <line x1="625" y1="217" x2="648" y2="217" stroke="#8b5cf6" strokeWidth="1.5" markerEnd="url(#arrowhead-violet)" />

    {/* ── Arrows: Row1 → Pipeline ── */}
    <line x1="160" y1="110" x2="160" y2="158" stroke="#8b5cf6" strokeWidth="1.5" markerEnd="url(#arrowhead-violet)" />
    <line x1="450" y1="110" x2="450" y2="158" stroke="#10b981" strokeWidth="1.5" markerEnd="url(#arrowhead-emerald)" />
    <line x1="740" y1="110" x2="740" y2="178" stroke="#0ea5e9" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
    <line x1="740" y1="178" x2="662" y2="178" stroke="#0ea5e9" strokeWidth="1" strokeDasharray="4 2" />

    {/* ── Row 3: Question Engine ── */}
    <rect x="180" y="300" width="540" height="90" rx="14" fill="#0ea5e9" fillOpacity="0.08" stroke="#0ea5e9" strokeWidth="1.5" />
    <text x="450" y="322" textAnchor="middle" fill="#7dd3fc" fontSize="12" fontWeight="700">QUESTION ENGINE</text>

    {/* Sub-boxes */}
    <rect x="200" y="335" width="130" height="44" rx="8" fill="#1e293b" stroke="#0ea5e9" strokeWidth="1" />
    <text x="265" y="353" textAnchor="middle" fill="#e2e8f0" fontSize="10" fontWeight="600">Render</text>
    <text x="265" y="367" textAnchor="middle" fill="#94a3b8" fontSize="9">Components</text>

    <rect x="350" y="335" width="130" height="44" rx="8" fill="#1e293b" stroke="#06b6d4" strokeWidth="1" />
    <text x="415" y="353" textAnchor="middle" fill="#e2e8f0" fontSize="10" fontWeight="600">Answer</text>
    <text x="415" y="367" textAnchor="middle" fill="#94a3b8" fontSize="9">Components</text>

    <rect x="500" y="335" width="100" height="44" rx="8" fill="#1e293b" stroke="#f59e0b" strokeWidth="1" />
    <text x="550" y="353" textAnchor="middle" fill="#e2e8f0" fontSize="10" fontWeight="600">Difficulty</text>
    <text x="550" y="367" textAnchor="middle" fill="#94a3b8" fontSize="9">Engine</text>

    <rect x="620" y="335" width="80" height="44" rx="8" fill="#1e293b" stroke="#10b981" strokeWidth="1" />
    <text x="660" y="353" textAnchor="middle" fill="#e2e8f0" fontSize="10" fontWeight="600">Answer</text>
    <text x="660" y="367" textAnchor="middle" fill="#94a3b8" fontSize="9">Validation</text>

    {/* Arrow: Pipeline → Question Engine */}
    <line x1="450" y1="250" x2="450" y2="298" stroke="#0ea5e9" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
    <text x="470" y="278" fill="#94a3b8" fontSize="9">Generated Questions</text>

    {/* ── Row 4: Outputs ── */}
    <rect x="80" y="440" width="180" height="60" rx="10" fill="#f59e0b" fillOpacity="0.12" stroke="#f59e0b" strokeWidth="1" />
    <text x="170" y="468" textAnchor="middle" fill="#fbbf24" fontSize="11" fontWeight="600">BASELINE TESTS</text>
    <text x="170" y="484" textAnchor="middle" fill="#94a3b8" fontSize="9">Cross-submodule coverage</text>

    <rect x="300" y="440" width="180" height="60" rx="10" fill="#0ea5e9" fillOpacity="0.12" stroke="#0ea5e9" strokeWidth="1" />
    <text x="390" y="468" textAnchor="middle" fill="#7dd3fc" fontSize="11" fontWeight="600">ADAPTIVE PRACTICE</text>
    <text x="390" y="484" textAnchor="middle" fill="#94a3b8" fontSize="9">Personalised learning paths</text>

    <rect x="520" y="440" width="180" height="60" rx="10" fill="#10b981" fillOpacity="0.12" stroke="#10b981" strokeWidth="1" />
    <text x="610" y="468" textAnchor="middle" fill="#6ee7b7" fontSize="11" fontWeight="600">SKILL HARDENING</text>
    <text x="610" y="484" textAnchor="middle" fill="#94a3b8" fontSize="9">Predecessor reinforcement</text>

    <rect x="740" y="440" width="120" height="60" rx="10" fill="#f43f5e" fillOpacity="0.12" stroke="#f43f5e" strokeWidth="1" />
    <text x="800" y="468" textAnchor="middle" fill="#fb7185" fontSize="11" fontWeight="600">MASTERY</text>
    <text x="800" y="484" textAnchor="middle" fill="#94a3b8" fontSize="9">Knowledge graph</text>

    {/* Arrows: Question Engine → Outputs */}
    <line x1="310" y1="390" x2="170" y2="438" stroke="#f59e0b" strokeWidth="1.2" markerEnd="url(#arrowhead-amber)" />
    <line x1="400" y1="390" x2="390" y2="438" stroke="#0ea5e9" strokeWidth="1.2" markerEnd="url(#arrowhead)" />
    <line x1="520" y1="390" x2="610" y2="438" stroke="#10b981" strokeWidth="1.2" markerEnd="url(#arrowhead-emerald)" />
    <line x1="660" y1="390" x2="790" y2="438" stroke="#f43f5e" strokeWidth="1.2" markerEnd="url(#arrowhead-violet)" />
  </svg>
);

// ─── SVG: Question Anatomy Diagram ───
const QuestionAnatomyDiagram: React.FC = () => (
  <svg viewBox="0 0 800 420" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <marker id="qa-arrow" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
        <polygon points="0 0, 8 3, 0 6" fill="#0ea5e9" />
      </marker>
      <marker id="qa-arrow-v" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
        <polygon points="0 0, 8 3, 0 6" fill="#8b5cf6" />
      </marker>
    </defs>

    {/* Question Cluster container */}
    <rect x="20" y="10" width="760" height="400" rx="16" fill="#0ea5e9" fillOpacity="0.04" stroke="#0ea5e9" strokeWidth="1" strokeDasharray="8 4" />
    <text x="400" y="38" textAnchor="middle" fill="#7dd3fc" fontSize="14" fontWeight="700">QUESTION CLUSTER (Multi-Part Question)</text>

    {/* Part 1 */}
    <rect x="50" y="60" width="330" height="160" rx="12" fill="#1e293b" stroke="#0ea5e9" strokeWidth="1.5" />
    <text x="215" y="85" textAnchor="middle" fill="#7dd3fc" fontSize="12" fontWeight="700">PART 1</text>

    {/* Render component */}
    <rect x="70" y="100" width="140" height="55" rx="8" fill="#0ea5e9" fillOpacity="0.1" stroke="#0ea5e9" strokeWidth="1" />
    <text x="140" y="122" textAnchor="middle" fill="#e2e8f0" fontSize="10" fontWeight="600">Render Component</text>
    <text x="140" y="140" textAnchor="middle" fill="#94a3b8" fontSize="9">LaTeX + Cartesian Grid</text>

    {/* Answer component */}
    <rect x="230" y="100" width="130" height="55" rx="8" fill="#8b5cf6" fillOpacity="0.1" stroke="#8b5cf6" strokeWidth="1" />
    <text x="295" y="122" textAnchor="middle" fill="#e2e8f0" fontSize="10" fontWeight="600">Answer Component</text>
    <text x="295" y="140" textAnchor="middle" fill="#94a3b8" fontSize="9">Multiple Choice</text>

    {/* Arrow between render and answer */}
    <line x1="210" y1="128" x2="228" y2="128" stroke="#0ea5e9" strokeWidth="1" markerEnd="url(#qa-arrow)" />

    {/* Knowledge nodes */}
    <rect x="70" y="168" width="290" height="40" rx="6" fill="#10b981" fillOpacity="0.08" stroke="#10b981" strokeWidth="0.8" />
    <text x="215" y="185" textAnchor="middle" fill="#6ee7b7" fontSize="9" fontWeight="600">Affects: Fractions (Y7) + Ratios (Y7)</text>
    <text x="215" y="198" textAnchor="middle" fill="#94a3b8" fontSize="8">Knowledge Nodes Updated on Resolution</text>

    {/* Part 2 */}
    <rect x="420" y="60" width="340" height="160" rx="12" fill="#1e293b" stroke="#8b5cf6" strokeWidth="1.5" />
    <text x="590" y="85" textAnchor="middle" fill="#c4b5fd" fontSize="12" fontWeight="700">PART 2</text>

    {/* Render component */}
    <rect x="440" y="100" width="140" height="55" rx="8" fill="#0ea5e9" fillOpacity="0.1" stroke="#0ea5e9" strokeWidth="1" />
    <text x="510" y="122" textAnchor="middle" fill="#e2e8f0" fontSize="10" fontWeight="600">Render Component</text>
    <text x="510" y="140" textAnchor="middle" fill="#94a3b8" fontSize="9">LaTeX Formula</text>

    {/* Answer component */}
    <rect x="600" y="100" width="140" height="55" rx="8" fill="#8b5cf6" fillOpacity="0.1" stroke="#8b5cf6" strokeWidth="1" />
    <text x="670" y="122" textAnchor="middle" fill="#e2e8f0" fontSize="10" fontWeight="600">Answer Component</text>
    <text x="670" y="140" textAnchor="middle" fill="#94a3b8" fontSize="9">Numeric Input</text>

    <line x1="580" y1="128" x2="598" y2="128" stroke="#8b5cf6" strokeWidth="1" markerEnd="url(#qa-arrow-v)" />

    {/* Knowledge nodes */}
    <rect x="440" y="168" width="300" height="40" rx="6" fill="#10b981" fillOpacity="0.08" stroke="#10b981" strokeWidth="0.8" />
    <text x="590" y="185" textAnchor="middle" fill="#6ee7b7" fontSize="9" fontWeight="600">Affects: Algebra (Y8)</text>
    <text x="590" y="198" textAnchor="middle" fill="#94a3b8" fontSize="8">Knowledge Node Updated on Resolution</text>

    {/* Connection arrow between parts */}
    <line x1="380" y1="140" x2="418" y2="140" stroke="#0ea5e9" strokeWidth="1.5" markerEnd="url(#qa-arrow)" />
    <text x="399" y="132" textAnchor="middle" fill="#94a3b8" fontSize="8">linked</text>

    {/* Bottom: Resolution row */}
    <rect x="120" y="250" width="560" height="50" rx="10" fill="#f59e0b" fillOpacity="0.1" stroke="#f59e0b" strokeWidth="1" />
    <text x="400" y="272" textAnchor="middle" fill="#fbbf24" fontSize="11" fontWeight="600">ALL ANSWER COMPONENTS MUST RESOLVE</text>
    <text x="400" y="289" textAnchor="middle" fill="#94a3b8" fontSize="9">Each component validates independently — cluster resolves when all parts pass</text>

    {/* Arrows down to resolution */}
    <line x1="215" y1="220" x2="300" y2="248" stroke="#f59e0b" strokeWidth="1" markerEnd="url(#arrowhead-amber)" />
    <line x1="590" y1="220" x2="500" y2="248" stroke="#f59e0b" strokeWidth="1" markerEnd="url(#arrowhead-amber)" />

    {/* Answer component types */}
    <text x="400" y="332" textAnchor="middle" fill="#7dd3fc" fontSize="12" fontWeight="700">ANSWER COMPONENT TYPES</text>
    <rect x="50" y="348" width="100" height="52" rx="8" fill="#1e293b" stroke="#0ea5e9" strokeWidth="0.8" />
    <text x="100" y="370" textAnchor="middle" fill="#e2e8f0" fontSize="9" fontWeight="600">Multiple</text>
    <text x="100" y="384" textAnchor="middle" fill="#94a3b8" fontSize="9">Choice</text>

    <rect x="168" y="348" width="100" height="52" rx="8" fill="#1e293b" stroke="#8b5cf6" strokeWidth="0.8" />
    <text x="218" y="370" textAnchor="middle" fill="#e2e8f0" fontSize="9" fontWeight="600">True / False</text>
    <text x="218" y="384" textAnchor="middle" fill="#94a3b8" fontSize="9">&nbsp;</text>

    <rect x="286" y="348" width="100" height="52" rx="8" fill="#1e293b" stroke="#10b981" strokeWidth="0.8" />
    <text x="336" y="370" textAnchor="middle" fill="#e2e8f0" fontSize="9" fontWeight="600">Numeric</text>
    <text x="336" y="384" textAnchor="middle" fill="#94a3b8" fontSize="9">Input</text>

    <rect x="404" y="348" width="100" height="52" rx="8" fill="#1e293b" stroke="#f59e0b" strokeWidth="0.8" />
    <text x="454" y="370" textAnchor="middle" fill="#e2e8f0" fontSize="9" fontWeight="600">Pairings</text>
    <text x="454" y="384" textAnchor="middle" fill="#94a3b8" fontSize="9">Match</text>

    <rect x="522" y="348" width="100" height="52" rx="8" fill="#1e293b" stroke="#06b6d4" strokeWidth="0.8" />
    <text x="572" y="370" textAnchor="middle" fill="#e2e8f0" fontSize="9" fontWeight="600">Interactive</text>
    <text x="572" y="384" textAnchor="middle" fill="#94a3b8" fontSize="9">Math Component</text>

    <rect x="640" y="348" width="110" height="52" rx="8" fill="#1e293b" stroke="#f43f5e" strokeWidth="0.8" />
    <text x="695" y="370" textAnchor="middle" fill="#e2e8f0" fontSize="9" fontWeight="600">Text / Free</text>
    <text x="695" y="384" textAnchor="middle" fill="#94a3b8" fontSize="9">Response</text>
  </svg>
);

// ─── SVG: AI Generation Flow ───
const AIGenerationFlowDiagram: React.FC = () => (
  <svg viewBox="0 0 840 340" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <marker id="ai-arrow" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
        <polygon points="0 0, 8 3, 0 6" fill="#8b5cf6" />
      </marker>
      <marker id="ai-arrow-cyan" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
        <polygon points="0 0, 8 3, 0 6" fill="#0ea5e9" />
      </marker>
      <marker id="ai-arrow-green" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
        <polygon points="0 0, 8 3, 0 6" fill="#10b981" />
      </marker>
    </defs>

    {/* Step 1: Query */}
    <rect x="20" y="20" width="150" height="90" rx="12" fill="#8b5cf6" fillOpacity="0.15" stroke="#8b5cf6" strokeWidth="1.5" />
    <text x="95" y="50" textAnchor="middle" fill="#c4b5fd" fontSize="10" fontWeight="600">STEP 1</text>
    <text x="95" y="70" textAnchor="middle" fill="#e2e8f0" fontSize="13" fontWeight="700">Query</text>
    <text x="95" y="88" textAnchor="middle" fill="#94a3b8" fontSize="9">User or system</text>
    <text x="95" y="100" textAnchor="middle" fill="#94a3b8" fontSize="9">request</text>

    <line x1="170" y1="65" x2="208" y2="65" stroke="#8b5cf6" strokeWidth="1.5" markerEnd="url(#ai-arrow)" />

    {/* Step 2: Understand Context */}
    <rect x="210" y="20" width="160" height="90" rx="12" fill="#0ea5e9" fillOpacity="0.15" stroke="#0ea5e9" strokeWidth="1.5" />
    <text x="290" y="50" textAnchor="middle" fill="#7dd3fc" fontSize="10" fontWeight="600">STEP 2</text>
    <text x="290" y="70" textAnchor="middle" fill="#e2e8f0" fontSize="13" fontWeight="700">Understand</text>
    <text x="290" y="88" textAnchor="middle" fill="#94a3b8" fontSize="9">Curriculum DB lookup</text>
    <text x="290" y="100" textAnchor="middle" fill="#94a3b8" fontSize="9">submodule + concept</text>

    <line x1="370" y1="65" x2="408" y2="65" stroke="#0ea5e9" strokeWidth="1.5" markerEnd="url(#ai-arrow-cyan)" />

    {/* Step 3: Search Components */}
    <rect x="410" y="20" width="160" height="90" rx="12" fill="#10b981" fillOpacity="0.15" stroke="#10b981" strokeWidth="1.5" />
    <text x="490" y="50" textAnchor="middle" fill="#6ee7b7" fontSize="10" fontWeight="600">STEP 3</text>
    <text x="490" y="70" textAnchor="middle" fill="#e2e8f0" fontSize="13" fontWeight="700">Search</text>
    <text x="490" y="88" textAnchor="middle" fill="#94a3b8" fontSize="9">Vector DB retrieval</text>
    <text x="490" y="100" textAnchor="middle" fill="#94a3b8" fontSize="9">best-fit components</text>

    <line x1="570" y1="65" x2="608" y2="65" stroke="#10b981" strokeWidth="1.5" markerEnd="url(#ai-arrow-green)" />

    {/* Step 4: Generate */}
    <rect x="610" y="20" width="210" height="90" rx="12" fill="#8b5cf6" fillOpacity="0.15" stroke="#8b5cf6" strokeWidth="1.5" />
    <text x="715" y="50" textAnchor="middle" fill="#c4b5fd" fontSize="10" fontWeight="600">STEP 4</text>
    <text x="715" y="70" textAnchor="middle" fill="#e2e8f0" fontSize="13" fontWeight="700">Generate</text>
    <text x="715" y="88" textAnchor="middle" fill="#94a3b8" fontSize="9">LLM assembles question set</text>
    <text x="715" y="100" textAnchor="middle" fill="#94a3b8" fontSize="9">using component schemas</text>

    {/* Arrow down from Generate */}
    <line x1="715" y1="110" x2="715" y2="148" stroke="#8b5cf6" strokeWidth="1.5" markerEnd="url(#ai-arrow)" />

    {/* Step 5: Validate */}
    <rect x="610" y="150" width="210" height="80" rx="12" fill="#f59e0b" fillOpacity="0.15" stroke="#f59e0b" strokeWidth="1.5" />
    <text x="715" y="178" textAnchor="middle" fill="#fbbf24" fontSize="10" fontWeight="600">STEP 5</text>
    <text x="715" y="198" textAnchor="middle" fill="#e2e8f0" fontSize="13" fontWeight="700">Validate</text>
    <text x="715" y="216" textAnchor="middle" fill="#94a3b8" fontSize="9">Engine checks all params solvable</text>

    {/* Arrow left from Validate */}
    <line x1="608" y1="190" x2="462" y2="190" stroke="#f59e0b" strokeWidth="1.5" markerEnd="url(#arrowhead-amber)" />

    {/* Output */}
    <rect x="260" y="155" width="200" height="70" rx="12" fill="#10b981" fillOpacity="0.15" stroke="#10b981" strokeWidth="1.5" />
    <text x="360" y="183" textAnchor="middle" fill="#6ee7b7" fontSize="10" fontWeight="600">OUTPUT</text>
    <text x="360" y="203" textAnchor="middle" fill="#e2e8f0" fontSize="13" fontWeight="700">Valid Question Set</text>
    <text x="360" y="216" textAnchor="middle" fill="#94a3b8" fontSize="9">Ready for student delivery</text>

    {/* Schema contract callout */}
    <rect x="120" y="260" width="600" height="65" rx="10" fill="#0ea5e9" fillOpacity="0.06" stroke="#0ea5e9" strokeWidth="1" strokeDasharray="6 3" />
    <text x="420" y="285" textAnchor="middle" fill="#7dd3fc" fontSize="11" fontWeight="700">JSON SCHEMA CONTRACT</text>
    <text x="420" y="305" textAnchor="middle" fill="#94a3b8" fontSize="10">Every component has a defined input/output schema — the LLM generates conforming data, the engine validates</text>
  </svg>
);

// ─── SVG: Model Compatibility ───
const ModelCompatibilityDiagram: React.FC = () => (
  <svg viewBox="0 0 800 260" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
    {/* Central requirement */}
    <rect x="250" y="10" width="300" height="60" rx="12" fill="#0ea5e9" fillOpacity="0.15" stroke="#0ea5e9" strokeWidth="1.5" />
    <text x="400" y="35" textAnchor="middle" fill="#7dd3fc" fontSize="11" fontWeight="600">CORE REQUIREMENT</text>
    <text x="400" y="55" textAnchor="middle" fill="#e2e8f0" fontSize="14" fontWeight="700">Structured Output (JSON Schema)</text>

    {/* Tier labels */}
    <text x="400" y="100" textAnchor="middle" fill="#94a3b8" fontSize="11" fontWeight="600">MODEL COMPATIBILITY SPECTRUM</text>

    {/* Tier 1: Optimal */}
    <rect x="30" y="120" width="220" height="120" rx="12" fill="#10b981" fillOpacity="0.12" stroke="#10b981" strokeWidth="1.5" />
    <text x="140" y="145" textAnchor="middle" fill="#6ee7b7" fontSize="11" fontWeight="700">OPTIMAL</text>
    <text x="140" y="168" textAnchor="middle" fill="#e2e8f0" fontSize="12" fontWeight="600">Higher Reasoning Models</text>
    <text x="140" y="188" textAnchor="middle" fill="#94a3b8" fontSize="10">Claude Opus / Sonnet, Gemini 3 Pro</text>
    <text x="140" y="206" textAnchor="middle" fill="#94a3b8" fontSize="10">GPT-5</text>
    <text x="140" y="226" textAnchor="middle" fill="#6ee7b7" fontSize="9">Best question quality + complexity</text>

    {/* Tier 2: Capable */}
    <rect x="290" y="120" width="220" height="120" rx="12" fill="#0ea5e9" fillOpacity="0.12" stroke="#0ea5e9" strokeWidth="1.5" />
    <text x="400" y="145" textAnchor="middle" fill="#7dd3fc" fontSize="11" fontWeight="700">CAPABLE</text>
    <text x="400" y="168" textAnchor="middle" fill="#e2e8f0" fontSize="12" fontWeight="600">Mid-Tier Models</text>
    <text x="400" y="188" textAnchor="middle" fill="#94a3b8" fontSize="10">Claude Haiku, Gemini 3 Flash</text>
    <text x="400" y="206" textAnchor="middle" fill="#94a3b8" fontSize="10">GPT-5 mini</text>
    <text x="400" y="226" textAnchor="middle" fill="#7dd3fc" fontSize="9">Good for standard question types</text>

    {/* Tier 3: Minimum */}
    <rect x="550" y="120" width="220" height="120" rx="12" fill="#f59e0b" fillOpacity="0.12" stroke="#f59e0b" strokeWidth="1.5" />
    <text x="660" y="145" textAnchor="middle" fill="#fbbf24" fontSize="11" fontWeight="700">MINIMUM VIABLE</text>
    <text x="660" y="168" textAnchor="middle" fill="#e2e8f0" fontSize="12" fontWeight="600">Lightweight / Local Models</text>
    <text x="660" y="188" textAnchor="middle" fill="#94a3b8" fontSize="10">Gemma 3, Phi-4</text>
    <text x="660" y="206" textAnchor="middle" fill="#94a3b8" fontSize="10">Other local / open-source</text>
    <text x="660" y="226" textAnchor="middle" fill="#fbbf24" fontSize="9">Simpler questions, validated output</text>

    {/* Gradient bar */}
    <rect x="30" y="248" width="740" height="6" rx="3" fill="url(#grad-emerald)" />
    <rect x="290" y="248" width="480" height="6" rx="3" fill="url(#grad-cyan)" />
    <rect x="550" y="248" width="220" height="6" rx="3" fill="url(#grad-amber)" />
  </svg>
);

// ─── SVG: Knowledge Hardening ───
const KnowledgeHardeningDiagram: React.FC = () => (
  <svg viewBox="0 0 800 300" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <marker id="kh-arrow" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
        <polygon points="0 0, 8 3, 0 6" fill="#10b981" />
      </marker>
      <marker id="kh-arrow-amber" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
        <polygon points="0 0, 8 3, 0 6" fill="#f59e0b" />
      </marker>
    </defs>

    {/* Year labels */}
    <text x="120" y="30" textAnchor="middle" fill="#94a3b8" fontSize="11" fontWeight="600">YEAR 5</text>
    <text x="400" y="30" textAnchor="middle" fill="#94a3b8" fontSize="11" fontWeight="600">YEAR 7</text>
    <text x="680" y="30" textAnchor="middle" fill="#94a3b8" fontSize="11" fontWeight="600">YEAR 8</text>

    {/* Year 5 nodes */}
    <circle cx="80" cy="80" r="28" fill="#10b981" fillOpacity="0.2" stroke="#10b981" strokeWidth="1.5" />
    <text x="80" y="76" textAnchor="middle" fill="#6ee7b7" fontSize="8" fontWeight="600">Basic</text>
    <text x="80" y="88" textAnchor="middle" fill="#6ee7b7" fontSize="8">Fractions</text>

    <circle cx="160" cy="80" r="28" fill="#10b981" fillOpacity="0.2" stroke="#10b981" strokeWidth="1.5" />
    <text x="160" y="76" textAnchor="middle" fill="#6ee7b7" fontSize="8" fontWeight="600">Whole</text>
    <text x="160" y="88" textAnchor="middle" fill="#6ee7b7" fontSize="8">Numbers</text>

    <circle cx="80" cy="160" r="28" fill="#0ea5e9" fillOpacity="0.2" stroke="#0ea5e9" strokeWidth="1.5" />
    <text x="80" y="156" textAnchor="middle" fill="#7dd3fc" fontSize="8" fontWeight="600">Basic</text>
    <text x="80" y="168" textAnchor="middle" fill="#7dd3fc" fontSize="8">Decimals</text>

    {/* Year 7 nodes */}
    <circle cx="360" cy="80" r="28" fill="#f59e0b" fillOpacity="0.2" stroke="#f59e0b" strokeWidth="1.5" />
    <text x="360" y="76" textAnchor="middle" fill="#fbbf24" fontSize="8" fontWeight="600">Fraction</text>
    <text x="360" y="88" textAnchor="middle" fill="#fbbf24" fontSize="8">Operations</text>

    <circle cx="440" cy="80" r="28" fill="#f59e0b" fillOpacity="0.2" stroke="#f59e0b" strokeWidth="1.5" />
    <text x="440" y="76" textAnchor="middle" fill="#fbbf24" fontSize="8" fontWeight="600">Ratios</text>
    <text x="440" y="88" textAnchor="middle" fill="#fbbf24" fontSize="8">&nbsp;</text>

    <circle cx="400" cy="160" r="28" fill="#0ea5e9" fillOpacity="0.2" stroke="#0ea5e9" strokeWidth="1.5" />
    <text x="400" y="156" textAnchor="middle" fill="#7dd3fc" fontSize="8" fontWeight="600">Decimal</text>
    <text x="400" y="168" textAnchor="middle" fill="#7dd3fc" fontSize="8">Arithmetic</text>

    {/* Year 8 node - target */}
    <circle cx="680" cy="120" r="35" fill="#8b5cf6" fillOpacity="0.25" stroke="#8b5cf6" strokeWidth="2" />
    <text x="680" y="112" textAnchor="middle" fill="#c4b5fd" fontSize="9" fontWeight="700">Linear</text>
    <text x="680" y="126" textAnchor="middle" fill="#c4b5fd" fontSize="9" fontWeight="700">Equations</text>
    <text x="680" y="144" textAnchor="middle" fill="#94a3b8" fontSize="7">TARGET</text>

    {/* Prerequisite arrows */}
    <line x1="108" y1="80" x2="330" y2="80" stroke="#10b981" strokeWidth="1.2" markerEnd="url(#kh-arrow)" />
    <line x1="188" y1="80" x2="330" y2="80" stroke="#10b981" strokeWidth="1.2" />
    <line x1="108" y1="160" x2="370" y2="160" stroke="#0ea5e9" strokeWidth="1.2" markerEnd="url(#ai-arrow-cyan)" />
    <line x1="468" y1="80" x2="643" y2="110" stroke="#f59e0b" strokeWidth="1.2" markerEnd="url(#kh-arrow-amber)" />
    <line x1="388" y1="80" x2="643" y2="110" stroke="#f59e0b" strokeWidth="1.2" />
    <line x1="428" y1="160" x2="650" y2="130" stroke="#0ea5e9" strokeWidth="1.2" markerEnd="url(#ai-arrow-cyan)" />

    {/* Hardening callout */}
    <rect x="100" y="220" width="600" height="65" rx="12" fill="#10b981" fillOpacity="0.08" stroke="#10b981" strokeWidth="1" />
    <text x="400" y="244" textAnchor="middle" fill="#6ee7b7" fontSize="12" fontWeight="700">AUTOMATIC HARDENING</text>
    <text x="400" y="264" textAnchor="middle" fill="#94a3b8" fontSize="10">Practising Year 8 Linear Equations automatically reinforces Year 5 Fractions, Decimals, and Year 7 prerequisites</text>
    <text x="400" y="278" textAnchor="middle" fill="#94a3b8" fontSize="10">AI generates predecessor questions to harden atrophied or forgotten skills</text>
  </svg>
);

const AiTech: React.FC = () => {
  const componentTypes = [
    {
      icon: DocumentTextIcon,
      title: 'Question Render Components',
      description: 'Display layer combining LaTeX mathematical notation, Cartesian grids, number lines, and rich text. Each component renders the question visually.',
      examples: ['LaTeX formulas', 'Cartesian grids', 'Number lines', 'Geometric shapes', 'Data tables'],
      color: 'var(--color-electric-blue)',
    },
    {
      icon: PuzzlePieceIcon,
      title: 'Answer Components',
      description: 'Interactive input layer where students provide responses. Each type has its own validation logic and can embed mathematical components in read-only mode.',
      examples: ['Multiple choice', 'True/false', 'Numeric input', 'Pairings/matching', 'Interactive math', 'Free text'],
      color: 'var(--color-violet)',
    },
    {
      icon: AdjustmentsHorizontalIcon,
      title: 'Difficulty Engine',
      description: 'Non-AI parametric system that determines value ranges, complexity, and constraints for each question type based on year level and difficulty setting.',
      examples: ['Parameter matrices', 'Year-level calibration', 'Value range selection', 'Complexity scaling', 'Component suitability filtering'],
      color: 'var(--color-amber)',
    },
  ];

  const vectorDbFeatures = [
    { label: 'Year Level', description: 'Which year levels the component is designed for' },
    { label: 'Submodule', description: 'Specific curriculum submodule it assesses' },
    { label: 'Pillar', description: 'Curriculum pillar (e.g. Number & Algebra)' },
    { label: 'Description', description: 'Full natural language description of the component' },
    { label: 'Input Schema', description: 'JSON Schema defining all accepted parameters' },
    { label: 'Output Schema', description: 'JSON Schema defining the answer validation contract' },
    { label: 'Answer Matrix', description: 'Which answer components are compatible' },
    { label: 'Embeddings', description: 'Semantic vector for similarity search' },
  ];

  const aiUseCases = [
    {
      icon: SparklesIcon,
      title: 'Question Generation',
      description: 'AI generates individual questions by selecting appropriate components, setting parameters within schema constraints, and producing valid question data.',
      gradient: 'from-[var(--color-violet)] to-[var(--color-electric-blue)]',
    },
    {
      icon: CubeTransparentIcon,
      title: 'Question Set Assembly',
      description: 'For multi-part questions spanning multiple submodules, AI selects and combines components that assess different concepts while maintaining pedagogical coherence.',
      gradient: 'from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)]',
    },
    {
      icon: MagnifyingGlassCircleIcon,
      title: 'Baseline Test Optimisation',
      description: 'AI designs diagnostic tests that maximise submodule coverage with minimal questions. Year 8 has 44 submodules — AI generates questions touching multiple submodules simultaneously.',
      gradient: 'from-[var(--color-emerald)] to-[var(--color-bright-cyan)]',
    },
    {
      icon: ArrowPathIcon,
      title: 'Predecessor Reinforcement',
      description: 'Based on mastery data, AI generates questions from prerequisite submodules to harden potentially atrophied skills that underpin current learning goals.',
      gradient: 'from-[var(--color-amber)] to-[var(--color-rose)]',
    },
    {
      icon: CommandLineIcon,
      title: 'Component Generation',
      description: 'AI is used to build the components themselves — creating new question and answer types that conform to the system\'s schema contracts and validation requirements.',
      gradient: 'from-[var(--color-rose)] to-[var(--color-violet)]',
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)]">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 glass-card px-4 py-2 rounded-full border border-[var(--color-violet)]/30 mb-6">
              <CpuChipIcon className="w-5 h-5 text-[var(--color-violet)]" />
              <span className="text-sm font-semibold text-[var(--color-violet)]">Technical Architecture</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
              AI-Powered <span className="text-gradient">Question Engine</span>
            </h1>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto mb-4">
              A model-agnostic architecture where AI generates curriculum-aligned questions through a contract-driven component system with programmatic validation.
            </p>
            <p className="text-[var(--color-medium-gray)] max-w-2xl mx-auto">
              Compatible with any LLM that supports structured output via JSON Schema — from cloud-hosted reasoning models to locally-run open-source models like Gemma 3.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* System Architecture Overview */}
      <section className="py-20 bg-[var(--color-dark-navy)]">
        <Container size="xl">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-4xl font-black text-white mb-4">
                System <span className="text-gradient">Architecture</span>
              </h2>
              <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
                The complete pipeline from AI model to student-facing questions, with validation at every stage
              </p>
            </div>
            <div className="card-premium p-8 lg:p-12">
              <SystemArchitectureDiagram />
            </div>
          </AnimatedSection>
        </Container>
      </section>

      {/* Question Engine Deep Dive */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container size="xl">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-4xl font-black text-white mb-4">
                Question Engine <span className="text-gradient">Anatomy</span>
              </h2>
              <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
                Every question is a composable unit — single or multi-part — where each component has defined contracts for rendering, answering, and knowledge node impact
              </p>
            </div>
            <div className="card-premium p-6 lg:p-10">
              <QuestionAnatomyDiagram />
            </div>
          </AnimatedSection>

          {/* Component Types Grid */}
          <div className="grid md:grid-cols-3 gap-8 mt-16">
            {componentTypes.map((type, index) => (
              <AnimatedSection key={type.title} delay={index * 0.1}>
                <div className="card-premium p-8 h-full hover-glow">
                  <type.icon className="w-12 h-12 mb-4" style={{ color: type.color }} />
                  <h3 className="text-xl font-bold text-white mb-3">{type.title}</h3>
                  <p className="text-[var(--color-light-gray)] mb-6">{type.description}</p>
                  <div className="space-y-2">
                    {type.examples.map((ex) => (
                      <div key={ex} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: type.color }} />
                        <span className="text-sm text-[var(--color-medium-gray)]">{ex}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* Key detail: Multiple choice */}
          <AnimatedSection className="mt-12">
            <div className="glass-card p-8 rounded-2xl border border-[var(--color-violet)]/20 max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <CheckBadgeIcon className="w-8 h-8 text-[var(--color-violet)] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-lg font-bold text-white mb-2">Component Composability</h4>
                  <p className="text-[var(--color-light-gray)]">
                    Each multiple choice option is itself a separate entity that can contain its own mathematical render component in read-only mode.
                    This means an answer option can display a LaTeX formula, a graph, or a geometric diagram — not just text.
                    The same composability applies to all answer types, enabling arbitrarily rich assessment formats while maintaining a consistent validation contract.
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </Container>
      </section>

      {/* Difficulty Engine */}
      <section className="py-20 bg-[var(--color-midnight)]">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[var(--color-amber)] to-[var(--color-rose)] p-4">
                  <AdjustmentsHorizontalIcon className="w-full h-full text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-white">Difficulty Engine</h2>
                  <p className="text-[var(--color-amber)]">Deterministic, Not AI-Driven</p>
                </div>
              </div>
              <p className="text-lg text-[var(--color-light-gray)] mb-6">
                Behind every question for every module, there is a difficulty engine calibrated per year level. This engine is <strong className="text-white">not AI-driven</strong> — it uses fine-tuned parameters based on aggregate student performance data.
              </p>
              <div className="space-y-4">
                {[
                  'Determines value ranges and numerical constraints based on difficulty setting',
                  'Maintains a matrix of compatible components per question type',
                  'Filters out inappropriate component combinations automatically',
                  'Parameters continuously refined based on overall cohort results',
                  'For AI-generated questions, difficulty is set by the AI input — the engine validates solvability',
                ].map((point) => (
                  <div key={point} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[var(--color-amber)] mt-2 flex-shrink-0" />
                    <span className="text-[var(--color-light-gray)]">{point}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="card-premium p-8">
                <h4 className="text-lg font-bold text-white mb-6 text-center">Difficulty Parameter Matrix</h4>
                <div className="space-y-4">
                  {[
                    { level: 'Very Easy', range: 'Single digit, positive integers', color: '#10b981' },
                    { level: 'Easy', range: 'Two digit, simple fractions', color: '#0ea5e9' },
                    { level: 'Medium', range: 'Mixed numbers, multi-step', color: '#3b82f6' },
                    { level: 'Hard', range: 'Negative values, complex fractions', color: '#8b5cf6' },
                    { level: 'Very Hard', range: 'Algebraic expressions, edge cases', color: '#f43f5e' },
                  ].map((d) => (
                    <div key={d.level} className="flex items-center gap-4">
                      <div className="w-28 flex-shrink-0">
                        <span className="text-sm font-semibold" style={{ color: d.color }}>{d.level}</span>
                      </div>
                      <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            backgroundColor: d.color,
                            width: d.level === 'Very Easy' ? '20%' : d.level === 'Easy' ? '40%' : d.level === 'Medium' ? '60%' : d.level === 'Hard' ? '80%' : '100%',
                          }}
                        />
                      </div>
                      <span className="text-xs text-[var(--color-medium-gray)] w-48 text-right">{d.range}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </Container>
      </section>

      {/* AI Generation Pipeline */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)]">
        <Container size="xl">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-4xl font-black text-white mb-4">
                AI Generation <span className="text-gradient-premium">Pipeline</span>
              </h2>
              <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
                How AI transforms a query into a validated, curriculum-aligned question set
              </p>
            </div>
            <div className="card-premium p-8 lg:p-12">
              <AIGenerationFlowDiagram />
            </div>
          </AnimatedSection>

          {/* AI Use Cases */}
          <div className="mt-16">
            <AnimatedSection>
              <h3 className="text-2xl font-bold text-white mb-8 text-center">Where AI Is Used</h3>
            </AnimatedSection>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {aiUseCases.map((uc, index) => (
                <AnimatedSection key={uc.title} delay={index * 0.08}>
                  <div className="card-premium p-6 h-full hover-glow">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${uc.gradient} p-3 mb-4`}>
                      <uc.icon className="w-full h-full text-white" />
                    </div>
                    <h4 className="text-lg font-bold text-white mb-2">{uc.title}</h4>
                    <p className="text-sm text-[var(--color-light-gray)]">{uc.description}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Vector Database */}
      <section className="py-20 bg-[var(--color-dark-navy)]">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[var(--color-emerald)] to-[var(--color-bright-cyan)] p-4">
                  <CircleStackIcon className="w-full h-full text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-white">Vector Database</h2>
                  <p className="text-[var(--color-emerald)]">Component Registry & Search</p>
                </div>
              </div>
              <p className="text-lg text-[var(--color-light-gray)] mb-6">
                All question and answer components are stored in a vector database with rich metadata. When AI needs to generate a question, it performs a semantic search to find the most appropriate components for the target concept, year level, and question type.
              </p>
              <p className="text-[var(--color-light-gray)]">
                Each component entry includes its full JSON schema contract — defining exactly what inputs it accepts and what outputs it produces. This is the bridge between AI creativity and system reliability.
              </p>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="card-premium p-6">
                <h4 className="text-lg font-bold text-white mb-4">Component Metadata</h4>
                <div className="space-y-3">
                  {vectorDbFeatures.map((f) => (
                    <div key={f.label} className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/5">
                      <span className="text-xs font-mono text-[var(--color-electric-blue)] bg-[var(--color-electric-blue)]/10 px-2 py-1 rounded flex-shrink-0 w-28 text-center">
                        {f.label}
                      </span>
                      <span className="text-sm text-[var(--color-light-gray)]">{f.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </Container>
      </section>

      {/* Knowledge Hardening */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container size="xl">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-4xl font-black text-white mb-4">
                Knowledge <span className="text-gradient">Hardening</span>
              </h2>
              <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
                Practising higher-level concepts automatically strengthens the prerequisite chain — AI identifies and reinforces atrophied skills
              </p>
            </div>
            <div className="card-premium p-8 lg:p-12">
              <KnowledgeHardeningDiagram />
            </div>
          </AnimatedSection>

          {/* Baseline testing callout */}
          <AnimatedSection className="mt-12">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="glass-card p-8 rounded-2xl border border-[var(--color-amber)]/20">
                <div className="flex items-start gap-4">
                  <BoltIcon className="w-8 h-8 text-[var(--color-amber)] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-lg font-bold text-white mb-2">Baseline Test Optimisation</h4>
                    <p className="text-[var(--color-light-gray)]">
                      Year 8 Maths has 44 submodules. Testing each individually would require at minimum 44 questions. AI generates questions that touch multiple submodules simultaneously, dramatically reducing the number of questions needed while maintaining comprehensive diagnostic coverage.
                    </p>
                  </div>
                </div>
              </div>
              <div className="glass-card p-8 rounded-2xl border border-[var(--color-emerald)]/20">
                <div className="flex items-start gap-4">
                  <ArrowPathIcon className="w-8 h-8 text-[var(--color-emerald)] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-lg font-bold text-white mb-2">Predecessor Reinforcement</h4>
                    <p className="text-[var(--color-light-gray)]">
                      When mastery data shows a student struggling with a higher-level concept, AI generates targeted questions from prerequisite submodules across previous years. This hardens the foundation before re-attempting the advanced material.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </Container>
      </section>

      {/* Model Compatibility */}
      <section className="py-20 bg-[var(--color-midnight)]">
        <Container size="xl">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-4xl font-black text-white mb-4">
                Model <span className="text-gradient-premium">Agnostic</span>
              </h2>
              <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
                The architecture works with any LLM that supports structured output via JSON Schema — plug in any model, from frontier to local
              </p>
            </div>
            <div className="card-premium p-8 lg:p-12">
              <ModelCompatibilityDiagram />
            </div>
          </AnimatedSection>

          <AnimatedSection className="mt-12">
            <div className="glass-card p-8 rounded-2xl border border-[var(--color-electric-blue)]/20 max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <ServerStackIcon className="w-8 h-8 text-[var(--color-electric-blue)] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-lg font-bold text-white mb-2">Why Model Agnostic?</h4>
                  <p className="text-[var(--color-light-gray)] mb-4">
                    Because every component interaction is governed by a strict JSON Schema contract, the system doesn't depend on any specific model's capabilities beyond structured output. The LLM's role is to make intelligent selections and fill in parameters — the engine handles all validation and rendering.
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-4 bg-white/5 rounded-lg text-center">
                      <div className="text-2xl font-bold text-[var(--color-violet)] mb-1">Schema</div>
                      <div className="text-xs text-[var(--color-medium-gray)]">Defines the contract</div>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg text-center">
                      <div className="text-2xl font-bold text-[var(--color-electric-blue)] mb-1">LLM</div>
                      <div className="text-xs text-[var(--color-medium-gray)]">Fills the contract</div>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg text-center">
                      <div className="text-2xl font-bold text-[var(--color-emerald)] mb-1">Engine</div>
                      <div className="text-xs text-[var(--color-medium-gray)]">Validates the contract</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </Container>
      </section>

      {/* Key Stats */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)]">
        <Container>
          <AnimatedSection>
            <div className="card-premium p-12">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-white mb-4">Architecture at a Glance</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {[
                  { value: 'JSON Schema', label: 'Contract System', desc: 'Every component defined' },
                  { value: 'Vector DB', label: 'Component Search', desc: 'Semantic retrieval' },
                  { value: 'Any LLM', label: 'Model Agnostic', desc: 'Structured output required' },
                  { value: 'Validated', label: 'Every Question', desc: 'Engine-verified solvable' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-2xl font-black text-gradient mb-2">{stat.value}</div>
                    <div className="text-white font-semibold mb-1">{stat.label}</div>
                    <div className="text-sm text-[var(--color-medium-gray)]">{stat.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </Container>
      </section>
    </>
  );
};

export default AiTech;
